import ms from "ms";
import * as React from "react";

import { useInterval } from "@/reporting/hooks/useInterval";
import { useCheckForceRefreshLazyQuery } from "./operations.generated";

/** How often to check for a new build ID. */
export const CHECK_INTERVAL = ms("1 hour");

/**
 * How long to wait before refreshing the page if the user has been inactive and
 * we're not told to force a refresh.
 */
export const SOFT_REFRESH_INTERVAL = ms("30 minutes");

/**
 * Waits for the user to be idle for a certain amount of time.
 */
function useWaitForIdle(idleTime: number): boolean {
  const [isIdle, setIsIdle] = React.useState(false);
  const lastInteraction = React.useRef(new Date());

  React.useEffect(() => {
    // See https://developer.mozilla.org/en-US/docs/Web/Security/User_activation
    const events = [
      "keydown",
      "mousedown",
      "pointerdown",
      "pointerup",
      "touchend",
    ];

    const handleInteraction = () => {
      lastInteraction.current = new Date();
      setIsIdle(false);
    };

    const handleIdle = () => {
      setIsIdle(true);
    };

    for (const event of events) {
      window.addEventListener(event, handleInteraction);
    }

    const interval = window.setInterval(() => {
      if (new Date().getTime() - lastInteraction.current.getTime() > idleTime) {
        handleIdle();
      }
    }, 1000);

    return () => {
      for (const event of events) {
        window.removeEventListener(event, handleInteraction);
      }

      window.clearInterval(interval);
    };
  }, [idleTime]);

  return isIdle;
}

/**
 * Slowly forces all users to be on the latest version of the app.
 *
 * This component checks periodically for the latest build version and a force
 * refresh flag. It reloads the page if a new build or a forced refresh is
 * detected.
 */
export function ForceRefresh() {
  const isIdle = useWaitForIdle(SOFT_REFRESH_INTERVAL);

  const [, { data, refetch }] = useCheckForceRefreshLazyQuery();

  const buildId = process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA ?? "local";
  const hasBuildIdChanged = Boolean(data?.buildId && data.buildId !== buildId);

  // Get the latest build ID and check if we should force a refresh.
  useInterval(async () => {
    await refetch().catch(() => {
      // Ignore errors
    });
  }, CHECK_INTERVAL);

  // Reload the page
  React.useEffect(() => {
    const shouldReload = data?.forceRefresh || (hasBuildIdChanged && isIdle);

    if (shouldReload) {
      window.location.reload();
    }
  }, [data?.forceRefresh, data?.buildId, hasBuildIdChanged, isIdle]);

  return null;
}
